import type { ElementState } from '../renderer/ElementState';
import type { ExtraElementData, VolumeKeyPoint } from '../types.ts/story';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

export const areVolumeKeyPointsSupportedBy = (
  element: ElementState,
): boolean => {
  return element.source?.type === 'audio';
};

export const getVolumeKeyPointsOf = (
  videoCreator: IVideoCreatorStore,
  element: ElementState,
): VolumeKeyPoint[] | undefined => {
  let result = (
    videoCreator.currentVideo?.extraElementData[
      element.source?.id
    ] as ExtraElementData
  )?.volumeKeyPoints;
  if (!areVolumeKeyPointsSupportedBy(element)) {
    result = undefined;
  }
  return result;
};

/**
 * Reconcile volume keypoints to an element's (possibly changed) duration: drop
 * points outside [0, duration], normalize their formatting, then make sure the
 * element's end is anchored by a keypoint.
 * - Shrink: tail points were dropped. Add an end anchor valued at the curve's
 *   interpolated volume at the cut, so the segment into the end keeps its
 *   original slope. Falls back to the closest past-the-cut keypoint's value
 *   when interpolation isn't defined.
 * - Grow: nothing past the end to drop. Move the existing end keypoint out to
 *   the new end rather than duplicating it with a flat-hold point.
 */
export const formatAndFixOffTimeVolumeKeyPoints = (
  volumeKeyPoints: VolumeKeyPoint[],
  duration: number,
): VolumeKeyPoint[] => {
  const fixedKeyPoints = volumeKeyPoints
    .filter(
      keyPoint =>
        parseFloat(keyPoint.time) >= 0 && parseFloat(keyPoint.time) <= duration,
    )
    .map(keyPoint => ({
      value: `${parseFloat(keyPoint.value)} %`,
      time: `${parseFloat(keyPoint.time)} s`,
    }));

  if (fixedKeyPoints.length > 0 && !reachesEnd()) {
    anchorElementEnd();
  }
  return fixedKeyPoints;

  function reachesEnd(): boolean {
    return fixedKeyPoints.some(
      keyPoint => Math.abs(parseFloat(keyPoint.time) - duration) < 0.0001,
    );
  }

  function anchorElementEnd(): void {
    const rightSide = volumeKeyPoints.filter(
      keyPoint => parseFloat(keyPoint.time) > duration,
    );
    if (rightSide.length) {
      // The curve's value at the cut keeps the slope of the segment that
      // crossed the new end; fall back to the closest past-the-cut keypoint's
      // value when interpolation isn't defined (e.g. clamped out of band).
      const interpolated = interpolateVolumeAtTime(
        { localTime: 0, duration },
        duration,
        volumeKeyPoints,
      );
      const value =
        interpolated ??
        parseFloat(
          rightSide.reduce((acc, keyPoint) =>
            parseFloat(keyPoint.time) < parseFloat(acc.time) ? keyPoint : acc,
          ).value,
        );
      fixedKeyPoints.push({
        value: `${value} %`,
        time: `${duration} s`,
      });
    } else {
      fixedKeyPoints[fixedKeyPoints.length - 1].time = `${duration} s`;
    }
  }
};

// Interpolate the audio volume between the two nearest keypoints at `time`.
// Returns null when there are fewer than 2 keypoints, when `time` is outside
// the element's range, when no later keypoint exists, or when the result is
// outside the renderable [0, 100) band.
export const interpolateVolumeAtTime = (
  element: { localTime: number; duration: number },
  time: number,
  volumeKeyPoints: VolumeKeyPoint[] | null | undefined,
): number | null => {
  if (
    !volumeKeyPoints ||
    volumeKeyPoints.length < 2 ||
    element.localTime > time ||
    element.localTime + element.duration < time
  )
    return null;

  const relativeTime = time - element.localTime;
  // Index of the LAST keypoint at or before `relativeTime` (keypoints are kept
  // sorted by time). Taking the last — not the first closest-from-below —
  // keeps "step" keypoints correct: two entries sharing a time with different
  // values are an instant jump, so once we're at/past that time the segment
  // must start from the post-step (second) entry. Picking the pre-step entry
  // instead makes `next` the coincident post-step point, collapsing the
  // segment to a flat hold and ignoring the jump.
  let prevIndex = -1;
  volumeKeyPoints.forEach((keyPoint, index) => {
    if (parseFloat(keyPoint.time) <= relativeTime) prevIndex = index;
  });
  if (prevIndex < 0) prevIndex = 0;
  const prev = volumeKeyPoints[prevIndex];
  const next = volumeKeyPoints[prevIndex + 1];
  if (!next) return null;

  const prevT = parseFloat(prev.time);
  const nextT = parseFloat(next.time);
  const prevV = parseFloat(prev.value);
  const nextV = parseFloat(next.value);
  const volume =
    nextT - prevT < 0.001
      ? prevV
      : prevV + ((nextV - prevV) * (relativeTime - prevT)) / (nextT - prevT);
  return volume >= 0 && volume < 100 ? volume : null;
};
